import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fmtINR, STATUS_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  CreditCard,
  TrendingUp,
  Calendar,
  Award,
  FileText,
  Eye,
  Download,
  AlertTriangle,
  CheckCircle2,
  Package,
  ArrowUpRight,
  RefreshCw,
  Zap,
  Landmark,
  Search,
  Building2,
} from "lucide-react";

const CATEGORY_COLORS = ["#0055FF", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

export default function DealerDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isStaff = ["admin", "manager", "sales", "accounts"].includes(user?.role);
  
  // Date Range State: Default to current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const todayStr = now.toISOString().slice(0, 10);

  const [dateRange, setDateRange] = useState({ from: firstDay, to: todayStr });
  const [selectedPreset, setSelectedPreset] = useState("this_month");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // For staff: list of authorized dealers
  const { data: dealers = [] } = useQuery({
    queryKey: ["dealers-list-scoped"],
    queryFn: async () => {
      const { data } = await api.get("/dealers");
      return Array.isArray(data) ? data : [];
    },
    enabled: isStaff,
    staleTime: 1000 * 60 * 10,
  });

  // Selected dealer ID for staff view
  const [selectedDealerId, setSelectedDealerId] = useState(
    searchParams.get("dealer_id") || (isStaff && dealers.length > 0 ? dealers[0].dealer_id : user?.dealer_id)
  );

  // Update selected dealer when dealers list loads if none selected
  useEffect(() => {
    if (isStaff && !selectedDealerId && dealers.length > 0) {
      const firstId = searchParams.get("dealer_id") || dealers[0].dealer_id;
      setSelectedDealerId(firstId);
    }
  }, [isStaff, dealers, selectedDealerId, searchParams]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const targetParam = isStaff && selectedDealerId ? `&dealer_id=${selectedDealerId}` : "";
      const { data } = await api.get(`/dashboard/stats?from_date=${dateRange.from}&to_date=${dateRange.to}${targetParam}`);
      setStats(data);
    } catch (err) {
      console.error("Dealer dashboard stats fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isStaff || selectedDealerId) {
      fetchStats();
    }
  }, [dateRange.from, dateRange.to, selectedDealerId]);

  const handleDealerChange = (dId) => {
    setSelectedDealerId(dId);
    setSearchParams({ dealer_id: dId });
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    const curr = new Date();
    if (preset === "today") {
      const d = curr.toISOString().slice(0, 10);
      setDateRange({ from: d, to: d });
    } else if (preset === "this_week") {
      const first = new Date(curr.setDate(curr.getDate() - curr.getDay() + 1)).toISOString().slice(0, 10);
      setDateRange({ from: first, to: todayStr });
    } else if (preset === "this_month") {
      const f = new Date(curr.getFullYear(), curr.getMonth(), 1).toISOString().slice(0, 10);
      setDateRange({ from: f, to: todayStr });
    } else if (preset === "last_30_days") {
      const past30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      setDateRange({ from: past30, to: todayStr });
    } else if (preset === "qtd") {
      const qMonth = Math.floor(curr.getMonth() / 3) * 3;
      const qStart = new Date(curr.getFullYear(), qMonth, 1).toISOString().slice(0, 10);
      setDateRange({ from: qStart, to: todayStr });
    }
  };

  if (loading && !stats) {
    return (
      <div className="space-y-6 p-1">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-44 rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="lg:col-span-2 h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  const creditUsedPct = stats?.credit_limit > 0 
    ? Math.min(100, Math.round((stats.current_outstanding / stats.credit_limit) * 100)) 
    : 0;

  return (
    <div className="space-y-8" data-testid="dealer-dashboard-page">
      {/* Staff Scoped Dealer Selector */}
      {isStaff && (
        <div className="bg-slate-900 text-white p-4 rounded-xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600/20 text-blue-400 rounded-lg border border-blue-500/30">
              <Building2 size={20} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Dealer Perspective Viewer
              </div>
              <div className="text-sm font-medium text-slate-200">
                Inspecting as role: <span className="capitalize font-semibold text-blue-400">{user?.role}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="dealer-select" className="text-xs text-slate-400 whitespace-nowrap">
              Select Dealer:
            </label>
            <select
              id="dealer-select"
              value={selectedDealerId || ""}
              onChange={(e) => handleDealerChange(e.target.value)}
              className="bg-slate-800 text-white text-xs font-medium border border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[260px]"
            >
              {dealers.map((d) => (
                <option key={d.dealer_id} value={d.dealer_id}>
                  {d.name} ({d.code || "TAMD0000"}) {d.area ? `• ${d.area}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Dealer Greeting Header & Date Filter Bar */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Dealer Portal</span>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-mono">
              {stats?.dealer_info?.code || user?.dealer_code || "TAMD0000"}
            </Badge>
          </div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight text-slate-900" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            {stats?.dealer_info?.name || user?.name}
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Real-time financial position, active promotional schemes, and sales order ledger.
          </p>
        </div>

        {/* Global Date Range Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-medium text-slate-600">
            <button
              onClick={() => handlePresetChange("this_month")}
              className={`px-3 py-1.5 rounded-md transition-all ${selectedPreset === "this_month" ? "bg-white text-blue-700 shadow-sm font-semibold" : "hover:text-slate-900"}`}
            >
              This Month
            </button>
            <button
              onClick={() => handlePresetChange("last_30_days")}
              className={`px-3 py-1.5 rounded-md transition-all ${selectedPreset === "last_30_days" ? "bg-white text-blue-700 shadow-sm font-semibold" : "hover:text-slate-900"}`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => handlePresetChange("qtd")}
              className={`px-3 py-1.5 rounded-md transition-all ${selectedPreset === "qtd" ? "bg-white text-blue-700 shadow-sm font-semibold" : "hover:text-slate-900"}`}
            >
              Quarter
            </button>
          </div>

          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs">
            <Calendar size={14} className="text-slate-400" />
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => { setSelectedPreset("custom"); setDateRange(prev => ({ ...prev, from: e.target.value })); }}
              className="text-xs font-medium text-slate-700 focus:outline-none bg-transparent"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => { setSelectedPreset("custom"); setDateRange(prev => ({ ...prev, to: e.target.value })); }}
              className="text-xs font-medium text-slate-700 focus:outline-none bg-transparent"
            />
          </div>

          <Button variant="outline" size="sm" onClick={fetchStats} className="h-9 w-9 p-0">
            <RefreshCw size={14} className={loading ? "animate-spin text-blue-600" : "text-slate-600"} />
          </Button>
        </div>
      </div>

      {/* Big 3 Financial & Order KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Current Outstanding & Credit Limit Gauge */}
        <Card className="p-5 border-slate-200 shadow-sm bg-gradient-to-b from-white to-slate-50/50">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Current Outstanding</div>
              <div className="mt-2 text-2xl font-bold tracking-tight text-amber-600 font-mono">
                {fmtINR(stats?.current_outstanding || 0)}
              </div>
            </div>
            <div className="p-2.5 rounded-xl bg-amber-50 text-amber-700 border border-amber-100">
              <CreditCard size={20} strokeWidth={1.5} />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-500">Credit Limit: <strong className="text-slate-800">{fmtINR(stats?.credit_limit || 0)}</strong></span>
              <span className="font-semibold text-emerald-600">Avail: {fmtINR(stats?.available_credit || 0)}</span>
            </div>
            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${creditUsedPct > 85 ? "bg-rose-500" : creditUsedPct > 60 ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: `${creditUsedPct}%` }}
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-1 flex justify-between">
              <span>{creditUsedPct}% credit line utilized</span>
              <Link to="/outstanding" className="text-blue-600 hover:underline flex items-center gap-0.5">
                Statement <ArrowUpRight size={10} />
              </Link>
            </div>
          </div>
        </Card>

        {/* This Month's Order Value */}
        <Card className="p-5 border-slate-200 shadow-sm bg-gradient-to-b from-white to-slate-50/50">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">This Month Orders</div>
              <div className="mt-2 text-2xl font-bold tracking-tight text-blue-600 font-mono">
                {fmtINR(stats?.this_month_order_value || 0)}
              </div>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-50 text-blue-700 border border-blue-100">
              <TrendingUp size={20} strokeWidth={1.5} />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
            <span>Submitted POs: <strong className="text-slate-900 font-medium">{stats?.this_month_po_count || 0}</strong></span>
            <Link to="/po/new" className="text-blue-600 hover:underline flex items-center gap-0.5 font-medium">
              Create PO <ArrowUpRight size={12} />
            </Link>
          </div>
        </Card>

        {/* Avg Monthly Order Value */}
        <Card className="p-5 border-slate-200 shadow-sm bg-gradient-to-b from-white to-slate-50/50">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Avg Monthly Order Value</div>
              <div className="mt-2 text-2xl font-bold tracking-tight text-slate-800 font-mono">
                {fmtINR(stats?.avg_monthly_order_value || 0)}
              </div>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100">
              <Zap size={20} strokeWidth={1.5} />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500 flex justify-between">
            <span>6-Month Rolling Benchmark</span>
            <span className="text-emerald-700 font-medium font-mono">Active Partner</span>
          </div>
        </Card>
      </div>

      {/* OPEN SCHEMES & VOLUME INCENTIVE TRACKER */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Incentive Programs</div>
            <h2 className="text-xl font-medium tracking-tight text-slate-900 mt-0.5" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
              Open Schemes & Target Slabs
            </h2>
          </div>
          <Link to="/schemes" className="text-xs text-blue-600 hover:underline flex items-center gap-1 font-medium">
            View all trade circulars <ArrowUpRight size={14} />
          </Link>
        </div>

        {(!stats?.open_schemes || stats.open_schemes.length === 0) ? (
          <Card className="p-8 text-center border-dashed border-slate-300">
            <Award size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm font-medium text-slate-700">No active schemes for your channel right now</div>
            <p className="text-xs text-slate-500 mt-1">Check back soon or speak with your Area Sales Manager for upcoming promotions.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.open_schemes.map((sc) => {
              const slabs = sc.payout_structure?.slabs || [
                { min_qty: 5, reward: "₹500 / unit" },
                { min_qty: 15, reward: "₹1,200 / unit" },
                { min_qty: 30, reward: "₹2,000 / unit" },
              ];
              const nextSlab = slabs.find(s => (s.min_qty || s.target) > sc.current_units) || slabs[slabs.length - 1];
              const targetUnits = nextSlab.min_qty || nextSlab.target || 20;
              const progressPct = Math.min(100, Math.round((sc.current_units / targetUnits) * 100));

              return (
                <Card key={sc.scheme_id} className="p-5 border-blue-100 bg-blue-50/20 relative overflow-hidden">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">
                        Open Scheme · Code: {sc.scheme_code || "SCH-01"}
                      </Badge>
                      <h3 className="font-semibold text-slate-900 text-base mt-1.5">{sc.title}</h3>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Valid till: <strong className="text-slate-700">{sc.valid_to || "End of month"}</strong>
                      </div>
                    </div>
                    {sc.file_url && (
                      <a 
                        href={sc.file_url} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm"
                        title="Download Circular PDF"
                      >
                        <Download size={16} />
                      </a>
                    )}
                  </div>

                  {/* Scheme Target Progress */}
                  <div className="mt-4 bg-white p-3.5 rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex justify-between text-xs mb-1 font-medium">
                      <span className="text-slate-600">Purchased Units: <strong className="text-blue-700">{sc.current_units} units</strong></span>
                      <span className="text-slate-900 font-semibold">Target: {targetUnits} units</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                      <div className="bg-blue-600 h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="flex justify-between items-center text-[11px] text-slate-500 mt-2">
                      <span>{Math.max(0, targetUnits - sc.current_units)} more units to unlock slab</span>
                      <span className="font-semibold text-emerald-700">{nextSlab.reward || nextSlab.benefit || "Cashback"}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-[11px] text-slate-500 truncate max-w-[220px]">
                      Eligible: {(sc.applicable_series || []).join(", ") || "All Galaxy Flagships"}
                    </div>
                    <Link to="/catalogue" className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1">
                      Order models <ArrowUpRight size={12} />
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* CONNECTED DATE-RANGE PERFORMANCE: Daywise Invoicing Graph & Category Share */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Performance Analytics</div>
            <h2 className="text-xl font-medium tracking-tight text-slate-900 mt-0.5" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
              Period Invoicing & Category Breakdown
            </h2>
          </div>
          <div className="text-xs text-slate-500 font-medium">
            Period Total: <strong className="text-slate-900">{fmtINR(stats?.period_order_value || 0)}</strong>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daywise Invoice Value Graph */}
          <Card className="lg:col-span-2 p-6 border-slate-200 shadow-sm">
            <div className="text-sm font-semibold text-slate-900 mb-1">Daywise Invoicing Trend</div>
            <p className="text-xs text-slate-500 mb-4">Daily billed invoice value for the selected period.</p>
            
            {(!stats?.daywise_invoice_graph || stats.daywise_invoice_graph.length === 0) ? (
              <div className="h-64 flex flex-col items-center justify-center text-slate-400 text-xs">
                <FileText size={32} strokeWidth={1} className="mb-2 text-slate-300" />
                No invoices recorded in this date range.
              </div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daywise_invoice_graph} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorInvoice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0055FF" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0055FF" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `₹${v >= 100000 ? (v/100000).toFixed(1) + 'L' : (v/1000).toFixed(0) + 'k'}`} />
                    <Tooltip 
                      formatter={(val) => [fmtINR(val), "Invoice Value"]} 
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "12px" }}
                    />
                    <Area type="monotone" dataKey="value" stroke="#0055FF" strokeWidth={2.5} fillOpacity={1} fill="url(#colorInvoice)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Category Share Donut */}
          <Card className="p-6 border-slate-200 shadow-sm flex flex-col">
            <div className="text-sm font-semibold text-slate-900 mb-1">Category Share</div>
            <p className="text-xs text-slate-500 mb-4">Volume & revenue split across product families.</p>

            {(!stats?.category_share || stats.category_share.length === 0) ? (
              <div className="h-64 flex flex-col items-center justify-center text-slate-400 text-xs my-auto">
                <Package size={32} strokeWidth={1} className="mb-2 text-slate-300" />
                No purchases in this range.
              </div>
            ) : (
              <div className="h-64 w-full my-auto">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.category_share}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                    >
                      {stats.category_share.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => fmtINR(val)} contentStyle={{ borderRadius: "8px", fontSize: "12px" }} />
                    <Legend wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* RECENT INVOICES (Last 20) WITH EMBEDDED MODAL / FRAME VIEWER */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Official SAP Invoices</div>
            <h2 className="text-xl font-medium tracking-tight text-slate-900 mt-0.5" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
              Recent Invoices & Billed Orders
            </h2>
          </div>
          <Badge variant="outline" className="text-xs text-slate-600 bg-slate-50">
            Last 20 records
          </Badge>
        </div>

        <Card className="overflow-hidden border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-3.5">Invoice / Order #</th>
                  <th className="text-left px-5 py-3.5">Linked PO</th>
                  <th className="text-left px-5 py-3.5">Billing Date</th>
                  <th className="text-right px-5 py-3.5">Items</th>
                  <th className="text-right px-5 py-3.5">Total Amount</th>
                  <th className="text-left px-5 py-3.5">Status</th>
                  <th className="text-right px-5 py-3.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(!stats?.recent_invoices || stats.recent_invoices.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16 text-slate-400 text-xs">
                      No invoices available for this counter.
                    </td>
                  </tr>
                ) : (
                  stats.recent_invoices.map((inv) => {
                    const isFulfilled = inv.status === "fulfilled" || inv.status === "approved";
                    return (
                      <tr key={inv.invoice_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5 font-mono text-xs font-semibold text-slate-800">
                          {inv.invoice_number}
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-blue-700">
                          <Link to={`/po/${inv.invoice_id}`} className="hover:underline">
                            {inv.po_number || inv.invoice_number}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate-600">
                          {new Date(inv.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-5 py-3.5 text-right text-xs font-mono text-slate-600">
                          {inv.item_count}
                        </td>
                        <td className="px-5 py-3.5 text-right font-medium text-slate-900">
                          {fmtINR(inv.amount)}
                        </td>
                        <td className="px-5 py-3.5">
                          <Badge variant="outline" className={isFulfilled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}>
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedInvoice(inv)}
                            className="h-8 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 gap-1.5"
                          >
                            <Eye size={14} /> View Invoice
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* EMBEDDED INVOICE VIEW MODAL / IFRAME */}
      <Dialog open={!!selectedInvoice} onOpenChange={(open) => !open && setSelectedInvoice(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-lg">
              <span>Official Invoice {selectedInvoice?.invoice_number}</span>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 font-mono text-xs">
                SAP Verified
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6 pt-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-lg border border-slate-100 text-xs">
                <div>
                  <div className="text-slate-400">Invoice Number</div>
                  <div className="font-mono font-semibold text-slate-800 mt-0.5">{selectedInvoice.invoice_number}</div>
                </div>
                <div>
                  <div className="text-slate-400">Billed Date</div>
                  <div className="font-medium text-slate-800 mt-0.5">{new Date(selectedInvoice.date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-slate-400">Total Billed</div>
                  <div className="font-semibold text-blue-700 mt-0.5">{fmtINR(selectedInvoice.amount)}</div>
                </div>
                <div>
                  <div className="text-slate-400">Payment Status</div>
                  <div className="font-medium text-emerald-700 mt-0.5 capitalize">{selectedInvoice.status}</div>
                </div>
              </div>

              {/* Embedded Document Frame simulation / live PDF */}
              <div className="border border-slate-200 rounded-lg p-6 bg-white shadow-inner font-sans space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                  <div>
                    <div className="font-bold text-base text-slate-900 tracking-tight">BHAWAR COMMERCIAL PRIVATE LIMITED</div>
                    <div className="text-xs text-slate-500">Authorized National Distributor · Samsung India Electronics</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-mono font-semibold text-slate-800">TAX INVOICE</div>
                    <div className="text-slate-500 font-mono">{selectedInvoice.invoice_number}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-slate-400 font-semibold uppercase text-[10px]">Billed To:</div>
                    <div className="font-bold text-slate-800 mt-1">{stats?.dealer_info?.name || user?.name}</div>
                    <div className="text-slate-600 font-mono text-[11px]">BP Code: {stats?.dealer_info?.code || "TAMD0000"}</div>
                    <div className="text-slate-500 mt-0.5">{stats?.dealer_info?.area} Region</div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400 font-semibold uppercase text-[10px]">Invoice Summary</div>
                    <div className="text-slate-600 mt-1">Currency: INR (₹)</div>
                    <div className="text-slate-600">Total Lines: {selectedInvoice.item_count} items</div>
                    <div className="text-slate-900 font-bold text-sm mt-1">{fmtINR(selectedInvoice.amount)}</div>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-md text-xs text-slate-500 text-center border border-dashed border-slate-200">
                  Document verified against SAP Business One server. To print or download standard GST e-Invoice with QR code, use the button below.
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" size="sm" onClick={() => setSelectedInvoice(null)}>
                  Close
                </Button>
                <Button size="sm" onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
                  <Download size={14} /> Print / Export Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
